import AwesomeDebouncePromise from 'awesome-debounce-promise';
import flatten from 'lodash/flatten';
import { v4 as uuid } from 'uuid';

import type {
    ServiceCreateForm,
    ServiceData,
    ServiceQuery,
    ServiceStatusUpdateForm,
    ServiceUpdateForm,
} from 'chaika-frontend-types/frontend-cms';
import type { ValidationParams } from '@components/types';
import { Locale, ServiceStatus } from 'chaika-enums';

import { store } from '@store';
import { ServiceApi } from '@api/ServiceApi';
import { SpecialtyApi } from '@api/SpecialtyApi';
import { CategoryApi } from '@api/CategoryApi';
import { getUniqNewIds } from '@utils/getUniqNewIds';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { validateServiceCreationParams, validateServiceUpdateParams } from '@services/utils/serviceValidation';

import { tagService } from './tagService';
import { COURSE_SERVICE_TAG_ID } from '@store/types';
import { translate } from '@chaika/i18n/helper';
import { isDefined } from '@utils/isDefined';
import { assertIsDefined } from '@utils/assertIsDefined';
import { PriceCreationParams } from '@store/models/Price';
import { priceService } from '@price/service/price/priceService';

export enum ServiceRelation {
    Tag = 'tag',
}

export interface CreateServiceParams extends ExtendedParams {
    params: ServiceCreateForm;
}

export interface UpdateServiceParams extends ExtendedParams {
    params: ServiceUpdateForm;
}

const findApi = AwesomeDebouncePromise(ServiceApi.getList, 500);

export interface ExtendedParams {
    basePrices?: PriceCreationParams[];
    specialtyIds?: string[];
    tagIds?: string[];
}

class ServiceService {
    private onLoadNext: boolean = false;

    private relationLoader = {
        [ServiceRelation.Tag]: tagService.load,
    };

    public async load(relations?: ServiceRelation[], withArchived?: boolean): Promise<void> {
        const {
            serviceStore: { serviceList },
            authUser: { selectedLanguage },
        } = store;
        const { limit, offset } = serviceList;

        if (offset === 0) {
            this.onLoadNext = true;

            const { count, services } = await ServiceApi.getList({
                limit,
                locale: withArchived ? Locale.Ru : selectedLanguage,
                statuses: withArchived ? `${ServiceStatus.Active},${ServiceStatus.Archived}` : undefined,
            });

            serviceList.addNext(services);
            serviceList.count = count;

            this.onLoadNext = false;
        }

        if (relations) {
            await this.loadRelations(relations);
        }
    }

    public async loadCategorySpecialties(): Promise<void> {
        const { serviceStore } = store;

        if (!serviceStore.categorySpecialtiesLoaded) {
            const categorySpecialties = await CategoryApi.getCategorySpecialities();
            serviceStore.setCategorySpecialties(categorySpecialties);
        }
    }

    public async loadNext(stopIndex = 0, withArchived?: boolean): Promise<void> {
        const searchList = store.serviceStore.searchList;

        if (searchList.searchParamsSet) {
            await this.loadNextSearchServiceList(stopIndex, withArchived);
        } else {
            await this.loadNextServiceList(stopIndex, withArchived);
        }
    }

    public async updateSearchParams(params: ServiceQuery, withArchived?: boolean): Promise<void> {
        const {
            serviceStore: { searchList },
            authUser: { selectedLanguage },
        } = store;
        searchList.setFilterParams({ ...params, offset: 0 });

        const { services, count } = searchList.searchParamsSet
            ? await findApi({
                  locale: withArchived ? Locale.Ru : selectedLanguage,
                  statuses: withArchived ? `${ServiceStatus.Active},${ServiceStatus.Archived}` : undefined,
                  withTranslations: true,
                  ...searchList.searchParams,
              })
            : { services: [], count: 0 };

        searchList.setResult(services, count);
    }

    public async loadByIds(ids: string[], lang?: Locale): Promise<void> {
        const {
            serviceStore: { serviceList },
            authUser: { selectedLanguage },
        } = store;

        const newIds = getUniqNewIds(serviceList.ids, ids);

        if (newIds.length > 0) {
            const chunkIds = getUniqChunkedIds(newIds);

            const servicesParams = await Promise.all(
                chunkIds.map(ids => ServiceApi.getList({ locale: lang ?? selectedLanguage, ids: ids.join(',') })),
            );

            let newCount = 0;
            servicesParams.forEach(({ services, count }) => {
                serviceList.add(services);
                newCount = newCount + count;
            });

            serviceList.count = serviceList.count + newCount;
        }
    }

    public async find(params: ServiceQuery): Promise<ServiceData[]> {
        const {
            authUser: { selectedLanguage },
        } = store;
        const { services } = await ServiceApi.getList({ locale: selectedLanguage, ...params });

        return services;
    }

    public async findAndAdd(params: ServiceQuery): Promise<ServiceData[]> {
        const {
            serviceStore: { serviceList },
            authUser: { selectedLanguage },
        } = store;

        const { services } = await ServiceApi.getList({ locale: selectedLanguage, ...params });

        serviceList.add(services);

        return services;
    }

    public async loadRelations(relations: ServiceRelation[]): Promise<void> {
        await Promise.all(relations.map(relation => this.relationLoader[relation]()));
    }

    public async create({
        basePrices,
        specialtyIds,
        tagIds,
        ...params
    }: ServiceCreateForm & ExtendedParams): Promise<void> {
        try {
            await ServiceApi.create(params);

            const serviceList = store.serviceStore.serviceList;

            const service = await ServiceApi.get(params.id);
            serviceList.add([service]);

            await this.loadExtendedData(params.id, { basePrices, specialtyIds, tagIds });
        } catch (e: any) {
            store.app.showPopup({
                title: translate('error'),
                description: e?.message ?? e,
            });
        }
    }

    public async update(id: string, { basePrices, specialtyIds, tagIds, params }: UpdateServiceParams): Promise<void> {
        try {
            await ServiceApi.update(id, { ...params });

            const current = this.getService(id);
            this.updateSerivice(id, { ...current, ...params } as ServiceData);
            await this.loadExtendedData(id, { basePrices, specialtyIds, tagIds });
        } catch (e: any) {
            store.app.showPopup({
                title: translate('error'),
                description: e?.message ?? e,
            });
        }
    }

    public async updateStatus(id: string, params: ServiceStatusUpdateForm): Promise<void> {
        const { serviceList, searchList } = store.serviceStore;

        await ServiceApi.updateStatus(id, params);

        const current = serviceList.get(id) || searchList.get(id);
        assertIsDefined(current, `Service ${id} must be loaded before its status is updated`);

        this.updateSerivice(id, { ...current, ...params });
    }

    public async updateTags(id: string, tagIds: string[]): Promise<void> {
        const current = this.getService(id);
        const currentTagIds = current?.tagIds || [];

        const removingIds = currentTagIds.filter(id => !tagIds.includes(id));
        const newIds = tagIds.filter(id => !currentTagIds.includes(id));

        for (const tagId of removingIds) {
            await ServiceApi.unbindTag(id, tagId);
        }

        for (const tagId of newIds) {
            await ServiceApi.bindTag(id, { tagId });
        }

        this.updateSerivice(id, { ...current, tagIds });
    }

    public async updateSpecialties(id: string, specialtyIds: string[]): Promise<void> {
        const current = this.getService(id);
        const currentSpecialtyIds = current?.specialtyIds || [];

        const removingIds = currentSpecialtyIds.filter(id => !specialtyIds.includes(id));
        const newIds = specialtyIds.filter(id => !currentSpecialtyIds.includes(id));

        for (const specialtyId of removingIds) {
            await SpecialtyApi.unbindService(specialtyId, id);
        }

        for (const specialtyId of newIds) {
            await SpecialtyApi.bindService(specialtyId, { serviceId: id });
        }

        this.updateSerivice(id, { ...current, specialtyIds });
    }

    public validateCreationParams(params: Partial<ServiceCreateForm>): ValidationParams {
        return validateServiceCreationParams(params);
    }

    public validateUpdateParams(params: Partial<ServiceUpdateForm>): ValidationParams {
        return validateServiceUpdateParams(params);
    }

    public isCourseService(serviceId: string): boolean {
        const service = store.serviceStore.serviceList.get(serviceId);

        return service?.tagIds?.some(id => id === COURSE_SERVICE_TAG_ID) ?? false;
    }

    private async loadNextServiceList(stopIndex = 0, withArchived?: boolean): Promise<void> {
        const {
            serviceStore: { serviceList },
            authUser: { selectedLanguage },
        } = store;

        if (!this.onLoadNext && serviceList.needLoadNext(stopIndex)) {
            this.onLoadNext = true;
            const params = serviceList.queryParams;
            const { services } = await ServiceApi.getList({
                ...params,
                locale: withArchived ? Locale.Ru : selectedLanguage,
                statuses: withArchived ? `${ServiceStatus.Active},${ServiceStatus.Archived}` : undefined,
            });

            serviceList.addNext(services);

            this.onLoadNext = false;
        }
    }

    private async loadNextSearchServiceList(stopIndex = 0, withArchived?: boolean): Promise<void> {
        const {
            serviceStore: { searchList },
            authUser: { selectedLanguage },
        } = store;

        if (!this.onLoadNext && searchList.needLoadNext(stopIndex)) {
            this.onLoadNext = true;

            const { services } = await ServiceApi.getList({
                locale: withArchived ? Locale.Ru : selectedLanguage,
                statuses: withArchived ? `${ServiceStatus.Active},${ServiceStatus.Archived}` : undefined,
                ...searchList.searchParams,
            });

            searchList.addNext(services);

            this.onLoadNext = false;
        }
    }

    private getService(id: string): ServiceData {
        const { serviceList, searchList } = store.serviceStore;

        return searchList.searchParamsSet ? (searchList.get(id) as ServiceData) : serviceList.getExisting(id);
    }

    private updateSerivice(id: string, params: ServiceData): void {
        const { serviceList, searchList } = store.serviceStore;

        if (serviceList.has(id)) {
            serviceList.update(id, params);
        }

        if (searchList.has(id)) {
            searchList.update(id, params);
        }
    }

    private async loadExtendedData(
        serviceId: string,
        { basePrices, specialtyIds, tagIds }: ExtendedParams,
    ): Promise<void> {
        // Если убрать все в Promise all Вызывает ошибку:
        // "EventListeners error: see causes for more info version_conflict_engine_exception"
        const preparedBasePrices = basePrices && this.prepareBasePrices(basePrices);
        await Promise.all([
            preparedBasePrices && preparedBasePrices.map(async basePrice => this.onCreateBasePrice(basePrice)),
        ]);

        if (isDefined(specialtyIds)) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            await serviceService.updateSpecialties(serviceId, specialtyIds);
        }
        if (isDefined(tagIds)) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            await serviceService.updateTags(serviceId, tagIds);
        }
    }

    private prepareBasePrices(basePrices: PriceCreationParams[]): PriceCreationParams[] {
        return flatten(
            basePrices.map(it => it.legalEntityIds.map(id => ({ ...it, id: uuid(), legalEntityIds: [id] }))),
        );
    }

    private async onCreateBasePrice(params: PriceCreationParams): Promise<void> {
        await priceService.addPrice(params);
    }
}

export const serviceService = new ServiceService();

export type { ServiceService };
